// La vista del trámite con varias cartas (CC-02, bloque B11; `ui/MG-ILE-1075.md`, «Vista del
// trámite»). Con una carta por autoridad destino (RN-77), «Estado del trámite» ya no puede decir
// «la carta»: dice el estado de CADA una, con el texto que deriva el servidor
// (`texto_de_la_carta`, `texto_del_acuse`), y la remisión del FAR-03 se registra por carta
// (RN-80), porque la ruta sin autoridad respondería 409 «indica la autoridad» (AT §11.3.11).
//
// Con UNA sola carta la vista del trámite no usa nada de esto: sigue como antes.

import { useState } from 'react';
import { FileCheck2 } from 'lucide-react';
import { Alert, Button, Field, Input, Mono } from './ui';
import EstadoDelAcuse from './EstadoDelAcuse';
import { comoFechaDeInstante, fechaDeHoyEnZona } from '../lib/fechas';
import { MENSAJE_CARTA_DESACTUALIZADA } from '../api/regulatorio';
import type { Tramite } from '../api/regulatorio';
import { accionesDeLaCarta } from '../api/cartas';
import type { VistaDeCarta } from '../api/cartas';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';

/** Las filas «Carta a SENASA», «Carta a SANIPES» de «Estado del trámite», dentro de su `dl`. */
export function FilasDeLasCartas({ cartas, zonaHoraria }: Readonly<{
  cartas: VistaDeCarta[];
  /** RN-82: la zona del país del caso (`useZonaDelPais`); `null` = Lima. */
  zonaHoraria: string | null;
}>) {
  return (
    <>
      {cartas.map((c) => (
        <div key={c.autoridad_codigo ?? ''} className="contents" data-testid="tramite-carta" data-autoridad={c.autoridad_codigo ?? ''}>
          <dt className="text-ink-soft">Carta a {c.autoridad_nombre}</dt>
          <dd className="flex flex-col items-start gap-1">
            <span>{c.texto_de_la_carta}</span>
            {c.carta_desactualizada && <span className="text-meta text-amber-700">{MENSAJE_CARTA_DESACTUALIZADA}</span>}
            {c.texto_del_acuse && <EstadoDelAcuse acuse={c} />}
            {c.fecha_presentacion && (
              <span className="text-meta text-ink-soft">Presentada el <Mono>{comoFechaDeInstante(c.fecha_presentacion, zonaHoraria)}</Mono></span>
            )}
          </dd>
        </div>
      ))}
    </>
  );
}

/** Una carta en «Registrar remisión del FAR-03»: ya remitido, o la fecha y el botón. */
function RemisionDeUnaCarta({ casoId, carta, zonaHoraria, onTramite }: Readonly<{
  casoId: string; carta: VistaDeCarta; zonaHoraria: string | null; onTramite: (t: Tramite) => void;
}>) {
  // RN-82: la remisión nace con el hoy del país del caso, no con el del navegador.
  const [fecha, setFecha] = useState(() => fechaDeHoyEnZona(zonaHoraria));
  const [ocupado, setOcupado] = useState(false);
  const [fallo, setFallo] = useState<Fallo | null>(null);

  const remitir = async () => {
    setFallo(null);
    setOcupado(true);
    try {
      onTramite(await accionesDeLaCarta(casoId, carta.autoridad_codigo).registrarRemision(fecha));
    } catch (e) {
      setFallo(aFallo(e));
    } finally {
      setOcupado(false);
    }
  };

  return (
    <li className="py-3" data-testid="tramite-remision" data-autoridad={carta.autoridad_codigo ?? ''}>
      {carta.fecha_remision_informe_final ? (
        <p className="text-body">FAR-03 remitido a {carta.autoridad_nombre} · <Mono>{comoFechaDeInstante(carta.fecha_remision_informe_final, zonaHoraria)}</Mono></p>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <Field label={`Fecha de remisión a ${carta.autoridad_nombre}`} required>
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </Field>
          <Button icon={FileCheck2} onClick={() => { remitir(); }} disabled={ocupado || !fecha} data-testid="tramite-remision-registrar">
            Registrar remisión a {carta.autoridad_nombre}
          </Button>
        </div>
      )}
      {fallo && (
        <div className="mt-2">
          <Alert tone="danger" title={`No se pudo registrar la remisión a ${carta.autoridad_nombre}`} detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert>
        </div>
      )}
    </li>
  );
}

/** HU-24-C12 por carta: la remisión del FAR-03 a cada autoridad destino. */
export function RemisionPorCarta({ casoId, cartas, zonaHoraria, onTramite }: Readonly<{
  casoId: string; cartas: VistaDeCarta[];
  /** RN-82: la zona del país del caso (`useZonaDelPais`); `null` = Lima. */
  zonaHoraria: string | null;
  onTramite: (t: Tramite) => void;
}>) {
  return (
    <ul className="divide-y divide-hairline" aria-label="Remisión del FAR-03 a cada autoridad">
      {cartas.map((c) => <RemisionDeUnaCarta key={c.autoridad_codigo ?? ''} casoId={casoId} carta={c} zonaHoraria={zonaHoraria} onTramite={onTramite} />)}
    </ul>
  );
}
